<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import FilterBar from 'dashboard/components-next/filter-bar/FilterBar.vue';
import PeriodPicker from 'dashboard/components-next/filter-bar/PeriodPicker.vue';
import { periodFromRange } from 'dashboard/components-next/filter-bar/period';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import {
  generateReportURLParams,
  hasBusinessHoursInboxes,
  parseReportURLParams,
} from '../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filterChange']);

const route = useRoute();
const router = useRouter();

// UI-FILTROS-01 (UF-46): o período do PeriodPicker, `{ range, from, to }`, com
// `from` e `to` em segundos, do início ao fim do dia. Sem período na URL, os
// últimos 7 dias, como antes.
const period = ref(periodFromRange(DATE_RANGE_TYPES.LAST_7_DAYS));
const businessHoursSelected = ref(false);

// Aqui não há filtro de caixa: o interruptor só existe quando alguma caixa da
// conta tem horário comercial. Fora disso ele some e vale desligado.
const inboxes = useMapGetter('inboxes/getInboxes');
const isBusinessHoursAvailable = computed(() =>
  hasBusinessHoursInboxes(inboxes.value)
);
const businessHours = computed(
  () => isBusinessHoursAvailable.value && businessHoursSelected.value
);

const updateURLParams = () => {
  const params = generateReportURLParams({
    from: period.value.from,
    to: period.value.to,
    businessHours: businessHours.value,
    range: period.value.range,
  });

  router.replace({ query: { ...params } });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: period.value.from,
    to: period.value.to,
    businessHours: businessHours.value,
  });
};

const onPeriodChange = value => {
  period.value = value;
  emitChange();
};

const onBusinessHoursToggle = () => {
  emitChange();
};

const initializeFromURL = () => {
  const urlParams = parseReportURLParams(route.query);

  // Com nome, o período da URL sempre traz as datas (`resolvePeriod`), levadas ao
  // início e ao fim do dia, como o PeriodPicker as quer.
  if (urlParams.range) {
    period.value = {
      range: urlParams.range,
      from: getUnixStartOfDay(new Date(urlParams.from * 1000)),
      to: getUnixEndOfDay(new Date(urlParams.to * 1000)),
    };
  }

  if (urlParams.businessHours) {
    businessHoursSelected.value = urlParams.businessHours;
  }
};

onMounted(() => {
  initializeFromURL();
  emitChange();
});

// Aberta direto pelo link, a tela monta antes de as caixas chegarem: quando o
// interruptor aparece (ou some), o business_hours da URL passa a valer (ou não).
watch(isBusinessHoursAvailable, () => {
  if (businessHoursSelected.value) emitChange();
});
</script>

<template>
  <FilterBar
    :model-value="{}"
    :search="false"
    :class="{ 'pointer-events-none opacity-50': disabled }"
  >
    <template #period>
      <PeriodPicker
        :model-value="period"
        @update:model-value="onPeriodChange"
      />
    </template>
    <template #toggles>
      <label
        v-if="isBusinessHoursAvailable"
        class="flex gap-2 items-center m-0 text-sm font-normal leading-normal cursor-pointer text-n-slate-11"
      >
        <ToggleSwitch
          v-model="businessHoursSelected"
          @change="onBusinessHoursToggle"
        />
        {{ $t('REPORT.BUSINESS_HOURS') }}
      </label>
    </template>
  </FilterBar>
</template>
